import React, {useState} from 'react';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomPagination from '../common/CustomPagination';
import SessionStatus from '../sessions/SessionStatus';
import {formatCaseName} from '../../helpers/cases';
import {formatPatientName} from '../../helpers/patients';
import {formatSessionDate, formatSessionTime} from '../../helpers/sessions';
import {useGetSessionsQuery, useUpdateSessionMutation} from '../../redux/services/sessionApi';

const handleUpdate = ({setUpdating, caseId, put}) => async sessionId => {
  setUpdating(true);
  const req = await put({id: sessionId, body: {case_id: caseId}});
  setUpdating(false);
};

const CaseSessionLinkDialog = props => {
  const {caseData: c, open, onClose} = props;
  const [pageIndex, setPageIndex] = useState(0);
  const [updating, setUpdating] = useState(false);
  const pageSize = 10;
  const sessionsQuery = useGetSessionsQuery(
    {
      patient_id: c?.patient_id, 
      include_nonowned_sessions: true,
      sort: '-date', 
      skip: pageIndex * pageSize, 
      limit: pageSize
    },
    {skip: !c?.patient_id}
  );
  const [put] = useUpdateSessionMutation();
  const sessions = sessionsQuery?.data?.data || [];
  const onAdd =  handleUpdate({setUpdating, put, caseId: c.id});
  const onRemove = handleUpdate({setUpdating, put, caseId: null});
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Stack spacing={2} justifyContent='center' alignItems='center'>
          <Typography variant='h3'>
            Link Sessions to Case
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Container maxWidth='md' style={{minHeight: '50vh'}}>
          <Stack spacing={1}>
            <Stack direction='row' spacing={1}>
              <Typography color='text.gray'>Patient:</Typography>
              <Typography>{formatPatientName(c.patient)}</Typography>
            </Stack>
            <Stack direction='row' spacing={1}>
              <Typography color='text.gray'>Case:</Typography>
              <Typography>{formatCaseName(c)}</Typography>
            </Stack>
            <Typography variant='h3' align='center' paddingTop={2}>Sessions</Typography>
            <Stack gap={0.5}>
              {sessions.map && sessions.map(s => 
                <Stack 
                  direction='row' 
                  gap={1} 
                  key={s.id} 
                  alignItems='center'
                  style={{minWidth: '275px'}}
                >
                  <SessionStatus session={s}/>
                  <Typography variant='body1' color='text.gray'>
                    <strong>
                      {formatSessionDate(s)} {formatSessionTime(s)}
                    </strong>
                  </Typography>
                  {(s.case_id === c.id || s?.case?.id === c.id)
                    ? <Button 
                      size='small' 
                      variant='text' 
                      color='error'
                      disabled={updating || sessionsQuery.isFetching}
                      onClick={() => onRemove(s.id)}
                    >
                      Remove
                    </Button>
                    : <Button 
                      size='small' 
                      variant='text'
                      disabled={updating || sessionsQuery.isFetching}
                      onClick={() => onAdd(s.id)}
                    >
                      Add
                    </Button>
                  }
                  {
                    updating || sessionsQuery.isFetching && <CircularProgress size={20}/>
                  }
                </Stack>
              )}
            </Stack>
            <CustomPagination 
              pageIndex={pageIndex}
              pageSize={pageSize}
              count={sessionsQuery?.data?.count || 0}
              onPageChange={(e,i) => setPageIndex(i)}
            />
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


const CaseSessionLinkingButton = props => {
  const {caseData: c} = props;
  const [isOpen, setDialogOpen] = useState(false);
  return (
    <>
      <CaseSessionLinkDialog
        open={isOpen}
        onClose={() => setDialogOpen(false)}
        caseData={c}
      />
      <Button 
        variant="text"
        onClick={() => setDialogOpen(true)}
      >
        Link Sessions to Case
      </Button>
    </>
  );
};

CaseSessionLinkingButton.propTypes = {
  caseData: PropTypes.object.isRequired,
}

export default CaseSessionLinkingButton;